// Route-guard logic for `requiresAuth` routes, split out of router/index.ts so it can be unit
// tested without constructing a router or Pinia store (I11).
//
// A fresh page load (e.g. opening /procurement directly) starts the router's first navigation
// before main.ts's auth.init() has fetched the signed-in user, so `user` is still null. Checking
// the role then would treat everyone as role-less and bounce role-gated deep links to the
// Dashboard. When a token exists but the user hasn't loaded, wait for the load first.
import { canAccessRoute } from '@/lib/permissions'

export interface AuthGuardState {
  token: string | null
  /** Read the user lazily — it only changes once `ensureUser` resolves. */
  getUser: () => { role?: string } | null
  ensureUser: () => Promise<void>
}

export type AuthGuardResult =
  | undefined
  | { name: 'login'; query: { redirect: string } }
  | { name: 'dashboard' }

export async function resolveAuthGuard(
  to: { path: string; fullPath: string },
  auth: AuthGuardState,
): Promise<AuthGuardResult> {
  if (!auth.token) return { name: 'login', query: { redirect: to.fullPath } }
  if (!auth.getUser()) {
    await auth.ensureUser()
    // The token was rejected (init clears it on a failed /auth/me): back to login, not Dashboard.
    const stillAuthed = auth.getUser()
    if (!stillAuthed) return { name: 'login', query: { redirect: to.fullPath } }
  }
  if (!canAccessRoute(to.path, auth.getUser()?.role)) return { name: 'dashboard' }
  return undefined
}
